import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import VideoWrapper from 'legacy-src/services/video/videoWrapper';
import { sendToTelemetry, TLTRY_DEFAULTS } from 'legacy-src/utility/telemetry';
import { PROVIDER_TYPES } from 'legacy-src/services/video/constants';
import { threePlayPluginChangeDefaults } from './helpers';
import get from 'lodash/get';

import './styles.scss';
import ThreePlayPlugin from './3PlayPlugin';

const VideoModal = (props) => {
  const [
    sendTelemetryIntermediatePlayEvent,
    setSendTelemetryIntermediatePlayEvent,
  ] = useState(false);
  const video = useRef(null);
  const interval = useRef(null);
  const {
    videoId,
    videoReference: threePlayVideoId,
    router,
    userData,
    eventCallbacks,
    progressTrackingKey,
    unitId,
    apcVideoId,
    videoPosition,
    progressVector,
    isImpersonating,
    apdCarouselData,
    setAPDCarouselData,
    isSPP,
    threeplayPluginFF,
  } = props;

  const [isScriptLoaded, setScriptLoaded] = useState(false);
  const [isScriptLoadFailed, setScriptLoadFailed] = useState(false);

  const {
    me: { initId: userId, sections },
    chosenEducationPeriodCode,
  } = userData;
  const { onPlay, onEnd, onPause, onPlaying, onClose } = eventCallbacks;

  const createInterval = () => {
    interval.current = setInterval(
      () => setSendTelemetryIntermediatePlayEvent(true),
      15000,
    );
  };

  const deleteInterval = () => {
    if (interval.current) {
      clearInterval(interval.current);
      setSendTelemetryIntermediatePlayEvent(false);
    }
  };

  const sendTelemetryEvent = (eventType, videoProgress) => {
    const { progress } = videoProgress || {};
    const { id: amUserId, pilot } = window.current_user || {};
    const { params } = router || {};
    const { subject: subjectId } = params || {};
    const sectionIds = sections
      .filter(
        (section) =>
          section.masterSubjectId === subjectId &&
          section.educationPeriod === chosenEducationPeriodCode,
      )
      .map((section) => section.id);

    const { fromToDoCard, fromExternalSharedLink } =
      router.location.state || {};
    const videoHandler = get(
      video.current,
      'videoProviderHandler.videoHandler',
    );
    const providerSessionId = videoHandler && videoHandler.eventKey();
    const data = {
      app: 'wizards/ap_daily_video',
      event_type: eventType,
      params: {
        video_id: apcVideoId,
        provider_video_id: videoId,
        session_id: window.localStorage.getItem('session_id'),
        provider_session_id: providerSessionId,
        provider: PROVIDER_TYPES.WISTIA,
        progress,
      },
      user: {
        am_user_id: amUserId,
        pilot,
        cb_user_id: userId,
      },
      context: {
        subject_id: subjectId,
        unit: unitId,
        from_todo_card: !!fromToDoCard,
        from_external_shared_link: !!fromExternalSharedLink,
        section_ids: sectionIds,
      },
      timestamp: dayjs(),
    };

    // ensure we don't send telemetry data when a teacher is impersonating a student
    if (!isImpersonating) {
      sendToTelemetry(Object.assign(data, TLTRY_DEFAULTS[0]));
    }
  };

  const setupCallbacks = () => {
    sendTelemetryEvent('launch');

    video.current = new VideoWrapper({
      videoId,
      userId,
      progressTrackingKey,
      time: videoPosition,
      progressVector,
      isImpersonating,
      isSPP,
    });

    const _onPlay = () => {
      const videoProgress = video.current.getProgressData();

      sendTelemetryEvent('play', videoProgress);
      createInterval();

      onPlay(videoProgress);
    };

    const _onEnd = () => {
      const videoProgress = video.current.getProgressData();

      sendTelemetryEvent('stop', videoProgress);
      deleteInterval();

      onEnd(videoProgress);
    };

    const _onPlaying = (isThresholdReached, newSecondsHaveElapsed) => {
      onPlaying(
        isThresholdReached,
        newSecondsHaveElapsed,
        video.current.getProgressData(),
      );
      if (isThresholdReached) {
        video.current.unbind('playing', _onPlaying);
      }
    };

    const _onPause = () => {
      const videoProgress = video.current.getProgressData();

      sendTelemetryEvent('stop', videoProgress);
      deleteInterval();

      onPause(videoProgress);
    };

    onPlay && video.current.bind('onplay', _onPlay);
    onPlaying && video.current.bind('playing', _onPlaying);
    onPause && video.current.bind('onpause', _onPause);
    onEnd && video.current.bind('onend', _onEnd);
    return { _onPlay, _onEnd, _onPlaying, _onPause };
  };

  const cleanupCallbacks = (_onPlay, _onPlaying, _onPause, _onEnd) => () => {
    if (video.current) {
      sendTelemetryEvent('close', video.current.getProgressData());
      onClose(video.current.getProgressData());

      onPlay && video.current.unbind('onplay', _onPlay);
      onPlaying && video.current.unbind('playing', _onPlaying);
      onPause && video.current.unbind('onpause', _onPause);
      onEnd && video.current.unbind('onend', _onEnd);
      video.current.destroy();
      video.current = null;
    }

    deleteInterval();

    // clear out whatever progress the teacher has saved locally while impersonating a student
    const { params } = router || {};
    const { subject: subjectId } = params || {};
    if (isImpersonating && apdCarouselData && apdCarouselData[subjectId]) {
      setAPDCarouselData({ subject: subjectId, videoData: null });
    }
  };

  useEffect(() => {
    if (!threeplayPluginFF || !threePlayVideoId || isScriptLoadFailed) {
      const { _onPlay, _onEnd, _onPlaying, _onPause } = setupCallbacks();
      return cleanupCallbacks(_onPlay, _onPlaying, _onPause, _onEnd);
    }
  }, []);

  useEffect(() => {
    if (threeplayPluginFF && isScriptLoaded) {
      threePlayPluginChangeDefaults();

      const { _onPlay, _onEnd, _onPlaying, _onPause } = setupCallbacks();
      return cleanupCallbacks(_onPlay, _onPlaying, _onPause, _onEnd);
    }
  }, [isScriptLoaded]);

  // Send telemetry event whenever the `sendTelemetryIntermediatePlayEvent` is `true`.
  useEffect(() => {
    if (sendTelemetryIntermediatePlayEvent) {
      video.current &&
        sendTelemetryEvent('play', video.current.getProgressData());
      setSendTelemetryIntermediatePlayEvent(false);
    }
  }, [sendTelemetryIntermediatePlayEvent]);

  const videoContainerId = `video_container_${videoId}`;

  const WistiaPlayer = (
    <div
      id={videoContainerId}
      className={`Video-embed--container wistia_embed wistia_async_${videoId} flex h-full`}
    >
      <LoadingSpinner containerClassName="flex" />
    </div>
  );

  return (
    <Fragment>
      {threeplayPluginFF ? (
        <>
          {/* lack of threePlayVideoId means video does not have AD or EAD */}
          {isScriptLoaded || isScriptLoadFailed || !threePlayVideoId ? (
            WistiaPlayer
          ) : (
            <LoadingSpinner containerClassName="flex" />
          )}
          <ThreePlayPlugin
            onLoad={() => setScriptLoaded(true)}
            onError={() => setScriptLoadFailed(true)}
            target={videoContainerId}
            videoId={videoId}
            threePlayVideoId={threePlayVideoId}
          />
        </>
      ) : (
        WistiaPlayer
      )}
    </Fragment>
  );
};

VideoModal.propTypes = {
  videoId: PropTypes.string.isRequired,
  router: PropTypes.object.isRequired,
  userData: PropTypes.object.isRequired,
  eventCallbacks: PropTypes.object.isRequired,
  progressTrackingKey: PropTypes.string,
  unitId: PropTypes.string,
  videoPosition: PropTypes.number.isRequired,
  apdCarouselData: PropTypes.object,
  setAPDCarouselData: PropTypes.func,
  isSPP: PropTypes.bool,
};

VideoModal.defaultProps = {
  onAssignClick: () => {},
  eventCallbacks: {},
  progressTrackingKey: null,
  unitId: null,
  videosCarousel: null,
  apdCarouselData: {},
  setAPDCarouselData: () => {},
  isSPP: false,
};

export default VideoModal;
